import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIButton from '@mui/material/Button';
import StarIcon from '@mui/icons-material/Star';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import CenterFlexBox from 'components/CenterFlexBox';

const FEATURES = [
    'AI-powered instant feedback',
    'All 4 Sections covered',
    'Realistic IELTS mock tests',
];

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        position: 'relative',
        padding: '80px 24px 64px',
        [theme.breakpoints.down('md')]: {
            padding: '48px 24px 40px',
        },
    },
    // Separate layer for the grid so we can fade it without affecting content
    gridBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: [
            'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
            'linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
        ].join(', '),
        backgroundSize: '60px 60px',
        WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 85%)',
        maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 85%)',
        pointerEvents: 'none',
        zIndex: 0,
    },
    content: {
        position: 'relative',
        zIndex: 1,
        width: '100%',
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: '50px',
        padding: '6px 16px',
        marginBottom: '28px',
        backgroundColor: 'rgba(255,255,255,0.05)',
    },
    badgeStar: {
        color: '#EE634E',
        fontSize: '0.85rem',
    },
    badgeText: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: '0.85rem',
        fontFamily: 'Mulish, sans-serif',
    },
    heading1: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 800,
        fontSize: '3.2rem',
        lineHeight: 1.15,
        textAlign: 'center',
        marginBottom: '8px',
        [theme.breakpoints.down('md')]: {
            fontSize: '2rem',
        },
    },
    heading2Wrapper: {
        padding: '4px 20px',
        marginBottom: '24px',
    },
    heading2: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 800,
        fontSize: '3.2rem',
        lineHeight: 1.2,
        textAlign: 'center',
        [theme.breakpoints.down('md')]: {
            fontSize: '2rem',
        },
    },
    subtitle: {
        color: '#E6E6E6',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '1rem',
        lineHeight: 1.6,
        maxWidth: '500px',
        textAlign: 'center',
        marginBottom: '32px',
    },
    ctaButton: {
        backgroundColor: '#EE634E',
        color: '#FFFFFF',
        borderRadius: '50px',
        padding: '12px 28px',
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.95rem',
        fontFamily: 'Mulish, sans-serif',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#d9563f',
            boxShadow: 'none',
        },
    },
    ctaArrow: {
        backgroundColor: '#EE634E',
        color: '#FFFFFF',
        borderRadius: '50%',
        minWidth: '44px',
        width: '44px',
        height: '44px',
        padding: 0,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#d9563f',
            boxShadow: 'none',
        },
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '50px',
        padding: '8px 16px',
    },
    featureIconWrapper: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#EE634E',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    featureStar: {
        color: '#FFFFFF',
        fontSize: '0.65rem',
        lineHeight: 1,
    },
    featureText: {
        color: 'rgba(255,255,255,0.9)',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.88rem',
        fontWeight: 500,
    },
    avatarImage: {
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        objectFit: 'cover',
        backgroundColor: 'rgba(255,255,255,0.15)',
        border: '2px solid #12111A',
        '&:not(:first-child)': {
            marginLeft: '-12px',
        },
    },
    starIcon: {
        color: '#FBBF24',
        fontSize: '1rem',
    },
    ratingScore: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.88rem',
        fontWeight: 700,
    },
    studentCount: {
        color: '#E6E6E6',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.8rem',
    },
}));

function Hero({
    badgeText = 'AI-Powered IELTS Preparation',
    heading1 = 'Stuck at Band 6.5?',
    heading2 = 'Boost Your IELTS Band Score.',
    subtitle = 'Real computer-based IELTS mock tests with AI-driven, actionable feedback.',
    ctaText = 'Check Your IELTS Band for Free',
    ctaHref = '/practice#practice-by-tests',
    features = FEATURES,
    noteText,
    bottomContent,
    hideCta = false,
    hideSocialProof = false,
}) {
    const classes = useStyles();

    return (
        <Box className={classes.root}>
            {/* Fading grid background layer */}
            <Box className={classes.gridBackground} />

            {/* All content sits above the grid */}
            <CenterFlexBox column className={classes.content}>

                {/* Badge */}
                <Box className={classes.badge}>
                    <Typography component="span" className={classes.badgeStar}>✳</Typography>
                    <Typography component="span" className={classes.badgeText}>
                        {badgeText}
                    </Typography>
                </Box>

                {/* Heading 1 */}
                <Typography className={classes.heading1}>
                    {heading1}
                </Typography>

                {/* Heading 2 — bordered box */}
                {heading2 && (
                    <Box className={classes.heading2Wrapper}>
                        <Typography className={classes.heading2}>
                            {heading2}
                        </Typography>
                    </Box>
                )}

                {/* Subtitle */}
                <Typography className={classes.subtitle}>
                    {subtitle}
                </Typography>

                {/* Optional note text */}
                {noteText && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '32px' }}>
                        <Typography component="span" sx={{ color: '#E6E6E6', fontSize: '0.85rem' }}>✓</Typography>
                        <Typography component="span" sx={{ color: '#E6E6E6', fontFamily: 'Mulish, sans-serif', fontSize: '0.85rem', fontStyle: 'italic' }}>
                            {noteText}
                        </Typography>
                    </Box>
                )}

                {/* CTA Buttons */}
                {!hideCta && (
                    <CenterFlexBox gap="8px" mb="40px">
                        <MUIButton
                            variant="contained"
                            disableElevation
                            component="a"
                            href={ctaHref}
                            className={classes.ctaButton}
                        >
                            {ctaText}
                        </MUIButton>
                        <MUIButton
                            variant="contained"
                            disableElevation
                            component="a"
                            href={ctaHref}
                            className={classes.ctaArrow}
                        >
                            <NorthEastIcon fontSize="small" />
                        </MUIButton>
                    </CenterFlexBox>
                )}

                {/* Feature chips */}
                {features.length > 0 && (
                    <CenterFlexBox gap="16px" flexWrap="wrap" mb="48px">
                        {features.map((feature) => (
                            <Box key={feature} className={classes.featureItem}>
                                <Box className={classes.featureIconWrapper}>
                                    <Typography component="span" className={classes.featureStar}>✳</Typography>
                                </Box>
                                <Typography component="span" className={classes.featureText}>
                                    {feature}
                                </Typography>
                            </Box>
                        ))}
                    </CenterFlexBox>
                )}

                {/* Social proof */}
                {!hideSocialProof && (
                    <CenterFlexBox gap="12px">
                        <CenterFlexBox>
                            {['ielts_hero1.jpg', 'ielts_hero2.jpg', 'ielts_hero3.jpg', 'ielts_hero4.jpg'].map((img) => (
                                <Box
                                    key={img}
                                    component="img"
                                    src={`https://assets.languify.in/images/${img}`}
                                    alt=""
                                    className={classes.avatarImage}
                                />
                            ))}
                        </CenterFlexBox>
                        <Box>
                            <CenterFlexBox gap="2px" justifyContent="flex-start">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <StarIcon key={i} className={classes.starIcon} />
                                ))}
                                <Typography component="span" className={classes.ratingScore} sx={{ ml: '4px' }}>
                                    4.8/5
                                </Typography>
                            </CenterFlexBox>
                            <Typography className={classes.studentCount}>
                                500+ Satisfied Students
                            </Typography>
                        </Box>
                    </CenterFlexBox>
                )}

                {/* Optional bottom content */}
                {bottomContent}

            </CenterFlexBox>
        </Box>
    );
}

export default Hero;
